import type { StudyDateFormat } from '../../../functions/src/studyProfile/studyDateFormat';

export interface StudySetup {
  // Protocol Identification
  protocolNumber?: string;
  therapeuticArea?: string;

  // Study Operations
  edcSystem: string;
  edcSystemOther?: string;
  randomizationSystem?: string;

  // Funding & Oversight
  sponsor: string;
  subInvestigators?: string;
  fundingAgency: string;
  fundingAgencyOther?: string;
  hasDSMB: 'yes' | 'no' | 'unknown';
  irbOfRecord?: string;

  // Reporting Requirements
  reportingRequirements: string[];

  // Regulatory
  indIdeNumber?: string;
  requiresPart11: 'yes' | 'no' | 'unknown';

  // Study Objectives
  primaryObjective?: string;

  // External Data Sources (user-declared, merged with auto-detected in EDTS)
  externalDataSources?: UserExternalDataSource[];

  // ── Study facts for generated documents (added 2026-09-30) ──────────────
  // Mirrors functions/src/models/studySetupTypes.ts. All optional; every
  // choice has 'unknown' = not yet decided, which documents show as TBD.
  countries?: string[];
  /** 'protocol' while `countries` was prefilled from the protocol and not yet confirmed or edited. */
  countriesSource?: 'protocol';
  /** Countries the protocol names that could not be matched to a code, for the user to pick the code. */
  countriesUnmapped?: string[];
  blinding?: BlindingSetting;
  randomized?: YesNoUnknown;
  productType?: ProductTypeSetting;
  oversight?: OversightSetting;
  edcVersion?: string;
  hostingProvider?: string;
  hostingRegion?: string;
  parties?: PartySetting[];
  safetyReporting?: SafetyReportingSetting;
  labModel?: 'local' | 'central' | 'mixed' | 'unknown';
  imagingModel?: 'site_read' | 'central_read' | 'none' | 'unknown';
  externalDataSourceExclusions?: ExternalDataSourceType[];
  ctmsInterface?: YesNoUnknown;
  /** Are protocol deviations recorded on an eCRF in the EDC? No = a separate deviation log. */
  protocolDeviationEcrf?: YesNoUnknown;
  ctmsName?: string;
  dictionaryVersions?: DictionaryVersionSettings;
  timelines?: TimelineSettings;
  subjectIdFormat?: string;
  /** Date format printed on the case report forms. Absent = the default, DD-MMM-YYYY. */
  crfDateFormat?: StudyDateFormat;
  cdiscStudyId?: string;
  studyParts?: StudyPartSetting[];
  /** Database lock design. Mirrors functions/src/models/studySetupTypes.ts LockPlanSetting; blank = platform default. */
  lockPlan?: LockPlanSetting;
}

export interface LockPlanSetting {
  model?: 'single_final' | 'interim_and_final' | 'default';
  lockApprovers?: string[];
  unlockRequesters?: string[];
  unlockApprovers?: string[];
  timingAfterLplv?: string;
}

export const LOCK_MODEL_OPTIONS = [
  { value: 'default', label: "Not set (use the study's other settings, otherwise one final lock)" },
  { value: 'single_final', label: 'Single final lock' },
  { value: 'interim_and_final', label: 'Interim and final locks' },
] as const;

/** One role per line in the form; blank text means "use the platform default" (undefined). */
export function rolesFromText(text: string): string[] | undefined {
  return text.trim() === '' ? undefined : text.split('\n');
}
export function rolesToText(roles: string[] | undefined): string {
  return (roles ?? []).join('\n');
}

export type YesNoUnknown = 'yes' | 'no' | 'unknown';
export type BlindingSetting = 'open_label' | 'single_blind' | 'double_blind' | 'triple_blind' | 'unknown';
export type OversightKindSetting = 'dsmb' | 'safety_review_committee' | 'steering_committee' | 'none' | 'unknown';
export interface OversightSetting {
  kind?: OversightKindSetting;
  name?: string;
  independent?: YesNoUnknown;
  membership?: string;
  cadence?: string;
}
export type PartyRole =
  | 'sponsor'
  | 'cro'
  | 'data_management'
  | 'biostatistics'
  | 'safety_pharmacovigilance'
  | 'edc_vendor'
  | 'medical_monitor'
  | 'central_lab'
  | 'imaging_vendor'
  | 'specialty_lab'
  | 'other';
export interface PartySetting {
  role: PartyRole;
  name: string;
  contact?: string;
}
export type SaeRouteSetting = 'paper_or_email_form' | 'edc_notification' | 'e2b_gateway' | 'unknown';
export interface SafetyReportingSetting {
  route?: SaeRouteSetting;
  description?: string;
  initialReportHours?: number;
  safetyDatabase?: string;
  safetyVendor?: string;
  reconciliationCadence?: string;
}
export interface DictionaryVersionSettings {
  meddra?: string;
  whodrug?: string;
  ctcae?: string;
  sdtmig?: string;
  cdash?: string;
}
export type DurationUnit = 'hours' | 'business_days' | 'calendar_days';
export interface DurationSetting {
  value: number;
  unit: DurationUnit;
}
export interface TimelineSettings {
  dataEntry?: DurationSetting;
  queryResponse?: DurationSetting;
  queryResponseNearLock?: DurationSetting;
  saeReconciliation?: string;
}
export interface StudyPartSetting {
  id: string;
  name?: string;
  inBuild: boolean;
}

/** Plain "not yet decided" wording shared by every select below. */
export const NOT_DECIDED_LABEL = 'Unknown / not yet decided';

export const COUNTRY_OPTIONS = [
  { value: 'AU', label: 'Australia' },
  { value: 'CA', label: 'Canada' },
  { value: 'EU', label: 'European Union / EEA' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'US', label: 'United States' },
] as const;

export type ProductTypeSetting = 'medicinal_product' | 'device' | 'combination' | 'other' | 'unknown';

export const PRODUCT_TYPE_OPTIONS: ReadonlyArray<{ value: ProductTypeSetting; label: string }> = [
  { value: 'unknown', label: NOT_DECIDED_LABEL },
  { value: 'medicinal_product', label: 'Medicinal product (drug or biologic)' },
  { value: 'device', label: 'Medical device' },
  { value: 'combination', label: 'Combination product' },
  { value: 'other', label: 'Other (e.g. behavioural or procedural)' },
];

/**
 * Help under "What the study tests" (2026-10-21). The choice sets the safety and regulatory rules
 * (functions/src/studyProfile/productFraming.ts), not only record retention.
 */
export const PRODUCT_TYPE_HELP =
  'Sets which safety and regulatory rules the documents and forms use. ' +
  'Medicinal product: drug rules (IND safety reports, SUSARs, DSUR, dose-change options). ' +
  'Medical device: device rules. Combination: both. ' +
  'Other: no drug or device rules; serious events go to the ethics committee. ' +
  'Not set: taken from the protocol, otherwise marked for sponsor review. ' +
  'Record retention is stated for medicinal products only; otherwise TBD.';

export const BLINDING_OPTIONS: ReadonlyArray<{ value: BlindingSetting; label: string }> = [
  { value: 'unknown', label: NOT_DECIDED_LABEL },
  { value: 'open_label', label: 'Open-label (no blinding)' },
  { value: 'single_blind', label: 'Single-blind' },
  { value: 'double_blind', label: 'Double-blind' },
  { value: 'triple_blind', label: 'Triple-blind' },
];

export const OVERSIGHT_KIND_OPTIONS: ReadonlyArray<{ value: OversightKindSetting; label: string }> = [
  { value: 'unknown', label: NOT_DECIDED_LABEL },
  { value: 'dsmb', label: 'Data safety monitoring board (DSMB / IDMC)' },
  { value: 'safety_review_committee', label: 'Safety review committee' },
  { value: 'steering_committee', label: 'Steering committee' },
  { value: 'none', label: 'None' },
];

export const PARTY_ROLE_OPTIONS: ReadonlyArray<{ value: PartyRole; label: string }> = [
  { value: 'sponsor', label: 'Sponsor' },
  { value: 'cro', label: 'Contract research organisation (CRO)' },
  { value: 'data_management', label: 'Data management' },
  { value: 'biostatistics', label: 'Biostatistics' },
  { value: 'safety_pharmacovigilance', label: 'Safety / pharmacovigilance' },
  { value: 'edc_vendor', label: 'EDC vendor' },
  { value: 'medical_monitor', label: 'Medical monitor' },
  { value: 'central_lab', label: 'Central laboratory' },
  { value: 'imaging_vendor', label: 'Imaging vendor' },
  { value: 'specialty_lab', label: 'Specialty laboratory' },
  { value: 'other', label: 'Other' },
];

export const SAE_ROUTE_OPTIONS: ReadonlyArray<{ value: SaeRouteSetting; label: string }> = [
  { value: 'unknown', label: NOT_DECIDED_LABEL },
  { value: 'paper_or_email_form', label: 'Site sends a report form (paper or email) to the safety group' },
  { value: 'edc_notification', label: 'The EDC notifies the safety group' },
  { value: 'e2b_gateway', label: 'Automatic electronic (E2B) transfer from the EDC' },
];

export const LAB_MODEL_OPTIONS = [
  { value: 'unknown', label: NOT_DECIDED_LABEL },
  { value: 'local', label: 'Local (site) laboratories' },
  { value: 'central', label: 'Central laboratory' },
  { value: 'mixed', label: 'Both local and central' },
] as const;

export const IMAGING_MODEL_OPTIONS = [
  { value: 'unknown', label: NOT_DECIDED_LABEL },
  { value: 'site_read', label: 'Images read at the site' },
  { value: 'central_read', label: 'Central imaging review' },
  { value: 'none', label: 'No study imaging' },
] as const;

export const DURATION_UNIT_OPTIONS: ReadonlyArray<{ value: DurationUnit; label: string }> = [
  { value: 'hours', label: 'hours' },
  { value: 'business_days', label: 'business days' },
  { value: 'calendar_days', label: 'calendar days' },
];

export const DICTIONARY_FIELDS: ReadonlyArray<{ key: keyof DictionaryVersionSettings; label: string; placeholder: string }> = [
  { key: 'meddra', label: 'MedDRA version', placeholder: 'Version approved by the sponsor' },
  { key: 'whodrug', label: 'WHODrug version', placeholder: 'Version approved by the sponsor' },
  { key: 'ctcae', label: 'CTCAE version', placeholder: 'If adverse events are graded' },
  { key: 'sdtmig', label: 'SDTMIG version', placeholder: 'For SDTM datasets' },
  { key: 'cdash', label: 'CDASH version', placeholder: 'For CRF standards' },
];

export interface UserExternalDataSource {
  type: ExternalDataSourceType;
  vendorName?: string;
  notes?: string;
}

export type ExternalDataSourceType =
  | 'central_lab'
  | 'irt'
  | 'epro'
  | 'imaging'
  | 'safety_db'
  | 'biomarker'
  | 'wearables'
  | 'ehr';

export const DEFAULT_STUDY_SETUP: StudySetup = {
  edcSystem: 'unknown',
  sponsor: '',
  fundingAgency: 'unknown',
  hasDSMB: 'unknown',
  reportingRequirements: [],
  requiresPart11: 'unknown',
};

export const EDC_SYSTEMS = [
  { value: 'medidata_rave', label: 'Medidata Rave' },
  { value: 'oracle_inform', label: 'Oracle Clinical / InForm' },
  { value: 'veeva_vault', label: 'Veeva Vault CDMS' },
  { value: 'redcap', label: 'REDCap' },
  { value: 'castor', label: 'Castor EDC' },
  { value: 'openclinica', label: 'OpenClinica' },
  { value: 'datatrak', label: 'Datatrak ONE' },
  { value: 'medrio', label: 'Medrio' },
  { value: 'dacima', label: 'Dacima' },
  { value: 'other', label: 'Custom / Other' },
  { value: 'unknown', label: 'Unknown / Not yet decided' },
] as const;

export const FUNDING_AGENCIES = [
  { value: 'nih_niaid', label: 'NIH / NIAID' },
  { value: 'nih_nci', label: 'NIH / NCI' },
  { value: 'nih_nhlbi', label: 'NIH / NHLBI' },
  { value: 'nih_nimh', label: 'NIH / NIMH' },
  { value: 'nih_other', label: 'NIH / Other Institute' },
  { value: 'dod_darpa', label: 'DoD / DARPA' },
  { value: 'barda', label: 'BARDA' },
  { value: 'fda', label: 'FDA' },
  { value: 'pcori', label: 'PCORI' },
  { value: 'gates', label: 'Gates Foundation' },
  { value: 'industry', label: 'Industry-funded (sponsor is funder)' },
  { value: 'institutional', label: 'Self-funded / Institutional' },
  { value: 'other', label: 'Other' },
  { value: 'unknown', label: 'Unknown' },
] as const;

export const EXTERNAL_DATA_SOURCE_OPTIONS = [
  { value: 'central_lab' as const, label: 'Central Laboratory' },
  { value: 'irt' as const, label: 'IRT / RTSM (Randomization)' },
  { value: 'epro' as const, label: 'ePRO / eCOA' },
  { value: 'imaging' as const, label: 'Imaging Core Lab' },
  { value: 'safety_db' as const, label: 'Safety Database' },
  { value: 'biomarker' as const, label: 'Biomarker Laboratory' },
  { value: 'wearables' as const, label: 'Wearables / Connected Devices' },
  { value: 'ehr' as const, label: 'EHR / Real-World Data' },
];

export const REPORTING_REQUIREMENTS = [
  { value: 'monthly_accrual', label: 'Monthly accrual reports' },
  { value: 'dsmb_reports', label: 'DSMB reports' },
  { value: 'interim_analyses', label: 'Interim analyses' },
  { value: 'annual_ind_ide', label: 'Annual IND/IDE safety reports' },
  { value: 'clinicaltrials_gov', label: 'ClinicalTrials.gov updates' },
  { value: 'sponsor_progress', label: 'Sponsor progress reports' },
  { value: 'fda_safety', label: 'FDA safety reporting (IND/MedWatch)' },
  { value: 'other', label: 'Other' },
] as const;
